import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';

/**
 * All payments, in every state.
 *
 * This is the reconciliation screen, not a sales report: it deliberately shows
 * `created` (checkout started, never completed) and `failed` alongside `paid`,
 * because those are the rows somebody has to chase. A school that says "I paid"
 * and a `created` row here is exactly the case this exists for.
 */
@Component({
  selector: 'app-payments-admin',
  templateUrl: './payments-admin.component.html',
  providers: [MessageService],
})
export class PaymentsAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Which document the screen is about. Same payments, different column set. */
  tab: 'receipts' | 'invoices' = 'receipts';

  rows: any[] = [];
  /** Set when the search box held a number from the old Download Invoice screen. */
  legacyRef: any = null;
  summary: any[] = [];
  invoiceSummary: any = null;
  loading = false;
  downloading: number | null = null;

  // Filters
  status = 'all';
  purpose = 'all';
  /** 'all' | 'online' (Razorpay) | 'offline' (recorded by ISSO). */
  mode = 'all';
  /**
   * Invoices tab only: 'all' | 'issued' | 'unbilled'.
   *
   * A number only exists once the invoice is issued, so an entry nobody has
   * billed cannot be found by its number - there isn't one. 'unbilled' is the
   * queue of those; 'issued' is the permanent record.
   */
  invoiceState = 'all';
  q = '';
  from = '';
  to = '';

  page = 1;
  pageSize = 25;
  total = 0;
  collected = 0;
  paidCount = 0;

  /** Row expanded to show the participant list. */
  expanded: number | null = null;

  /** Live Razorpay result per payment row, keyed by payment id. */
  checks: { [id: number]: any } = {};
  checking: number | null = null;
  settling: number | null = null;

  scanOpen = false;
  scanDays = 7;
  scanning = false;
  scanResult: any = null;

  lookupOpen = false;
  lookupRef = '';
  lookupDate = '';
  lookingUp = false;
  lookupResult: any = null;

  readonly verdictText: { [k: string]: string } = {
    recorded:   'Recorded as paid on the platform.',
    settle:     'Money captured at Razorpay but not recorded here. Settle it to mark the entry paid and issue the receipt.',
    duplicate:  'Captured at Razorpay, but this entry is already paid by another order - the parent was charged twice. Refund this payment from the Razorpay dashboard.',
    authorized: 'Money is held but not captured. Capture it in the Razorpay dashboard, then check again.',
    refunded:   'Razorpay refunded this payment - the money went back to the payer. Nothing was received.',
    failed:     'Every payment attempt on this order failed. Nothing was received.',
    abandoned:  'No payment was attempted on this order (checkout closed). Nothing was received.',
    'no-order': 'This payment has no Razorpay order to check.',
  };

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); this.loadSummary(); }

  setTab(t: 'receipts' | 'invoices') {
    if (this.tab === t) return;
    this.tab = t;
    this.expanded = null;
    /**
     * The status filter does not carry across.
     *
     * A receipt only exists for a settled payment, so that tab is usually read
     * with Paid selected - and carrying that into Invoices would hide every
     * unpaid bill, which is the whole reason to open it.
     */
    this.status = 'all';
    this.invoiceState = 'all';
    this.page = 1;
    this.load();
    if (t === 'invoices' && !this.invoiceSummary) this.loadInvoiceSummary();
  }

  loadInvoiceSummary() {
    this.svc.invoiceSummary().pipe(takeUntil(this.destroy$))
      .subscribe((r: any) => { this.invoiceSummary = r; }, () => { /* tiles are optional */ });
  }

  get pages(): number { return Math.max(1, Math.ceil(this.total / this.pageSize)); }

  load() {
    this.loading = true;
    this.svc
      .payments({
        status: this.status, purpose: this.purpose, mode: this.mode, q: this.q,
        from: this.from, to: this.to, page: this.page, pageSize: this.pageSize,
        // Only meaningful on the Invoices tab; omitted elsewhere so the
        // Receipts list is unaffected by a filter it does not show.
        invoice: this.tab === 'invoices' ? this.invoiceState : 'all',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (r: any) => {
          this.rows = r.rows || [];
          this.legacyRef = r.legacyRef || null;
          this.total = r.total || 0;
          this.collected = r.collected || 0;
          this.paidCount = r.paidCount || 0;
          this.loading = false;
        },
        () => { this.loading = false; this.err('Failed to load payments'); },
      );
  }

  loadSummary() {
    this.svc.paymentSummary().pipe(takeUntil(this.destroy$))
      .subscribe((r: any) => { this.summary = r || []; }, () => { /* tiles are optional */ });
  }

  search()               { this.page = 1; this.load(); }
  setStatus(s: string)   { this.status = s; this.page = 1; this.load(); }
  setPurpose(p: string)  { this.purpose = p; this.page = 1; this.load(); }
  setMode(m: string)     { this.mode = m; this.page = 1; this.load(); }
  setInvoiceState(s: string) { this.invoiceState = s; this.page = 1; this.load(); }
  go(p: number)          { if (p >= 1 && p <= this.pages) { this.page = p; this.load(); } }
  toggle(id: number)     { this.expanded = this.expanded === id ? null : id; }

  clearFilters() {
    this.status = 'all'; this.purpose = 'all'; this.mode = 'all'; this.invoiceState = 'all';
    this.q = ''; this.from = ''; this.to = '';
    this.page = 1; this.load();
  }

  countFor(status: string): number {
    const hit = this.summary.filter(s => s.status === status)[0];
    return hit ? hit.count : 0;
  }
  amountFor(status: string): number {
    const hit = this.summary.filter(s => s.status === status)[0];
    return hit ? hit.amount : 0;
  }

  /**
   * Receipts are behind auth, so this cannot be a plain href - the token has to
   * ride on the request. Fetch as a blob and hand it to the browser.
   */
  download(row: any) {
    if (!row.receiptAvailable) return;
    this.downloading = row.id;
    this.svc.paymentReceipt(row.id).pipe(takeUntil(this.destroy$)).subscribe(
      (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (row.receiptNo || 'receipt-' + row.id).replace(/[^\w.-]+/g, '-') + '.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.downloading = null;
      },
      () => { this.downloading = null; this.err('Could not download the receipt'); },
    );
  }

  /* ── Invoices ───────────────────────────────────────────────────────── */

  downloadingInvoice: number | null = null;

  downloadInvoice(row: any) {
    this.downloadingInvoice = row.id;
    this.svc.paymentInvoice(row.id).pipe(takeUntil(this.destroy$)).subscribe(
      (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (row.invoiceNo || 'invoice-' + row.id).replace(/[^\w.-]+/g, '-') + '.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.downloadingInvoice = null;
        // The number is allocated on first download, so the row is now stale.
        if (!row.invoiceNo) this.load();
      },
      () => { this.downloadingInvoice = null; this.err('Could not download the invoice'); },
    );
  }

  /**
   * Issue the number without downloading.
   *
   * The number used to be a side-effect of clicking Download, which left an
   * entry nobody opened with no number to search for. Issuing is now the act,
   * and the PDF is a separate choice.
   */
  issuing: number | null = null;

  issue(row: any) {
    this.issuing = row.id;
    this.svc.issueInvoice(row.id).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.issuing = null;
        row.invoiceNo = r.invoiceNo;
        row.invoiceIssued = true;
        this.toast.add({ key: 'custom', severity: 'success', summary: 'Invoice issued', detail: r.invoiceNo });
        this.loadInvoiceSummary();
        // The row falls out of the list when the queue is what is on screen.
        if (this.invoiceState === 'unbilled') this.load();
      },
      (e: any) => { this.issuing = null; this.err(this.msg(e, 'Could not issue the invoice')); },
    );
  }

  issuingAll = false;

  /** Number every unbilled entry on this page. Deliberately page-at-a-time:
      the admin sees exactly what is about to be billed. */
  issueAllOnPage() {
    const ids = this.rows.filter(r => !r.invoiceNo).map(r => r.id);
    if (!ids.length) return;
    if (!confirm('Issue ' + ids.length + ' invoice number' + (ids.length === 1 ? '' : 's') +
                 '? Numbers come from the running series and cannot be withdrawn.')) return;
    this.issuingAll = true;
    this.svc.issueInvoices(ids).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.issuingAll = false;
        const n = (r.issued || []).length;
        const bad = (r.failed || []).length;
        this.toast.add({
          key: 'custom',
          severity: bad ? 'warn' : 'success',
          summary: n + ' invoice' + (n === 1 ? '' : 's') + ' issued',
          detail: bad ? bad + ' could not be issued' : undefined,
        });
        this.load();
        this.loadInvoiceSummary();
      },
      (e: any) => { this.issuingAll = false; this.err(this.msg(e, 'Could not issue the invoices')); },
    );
  }

  get unbilledOnPage(): number {
    return this.rows.filter(r => !r.invoiceNo).length;
  }

  /* Send dialog. Mailing a school is outward-facing, so the address is shown
     and confirmed rather than guessed at silently. */
  sendOpen = false;
  sendRow: any = null;
  sendTo = '';
  sendNote = '';
  sendOptions: string[] = [];
  sending = false;

  openSend(row: any) {
    this.sendRow = row;
    this.sendTo = '';
    this.sendNote = '';
    this.sendOptions = [];
    this.sendOpen = true;
    this.svc.invoiceRecipients(row.id).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.sendOptions = r.options || []; this.sendTo = r.to || ''; },
      () => { this.sendOptions = []; },
    );
  }

  closeSend() { if (!this.sending) { this.sendOpen = false; this.sendRow = null; } }

  confirmSend() {
    if (!this.sendRow) return;
    const to = (this.sendTo || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { this.err('Enter a valid email address'); return; }
    this.sending = true;
    this.svc.sendInvoice(this.sendRow.id, { to, note: (this.sendNote || '').trim() || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.sending = false;
          this.sendOpen = false;
          this.toast.add({ key: 'custom', severity: 'success', summary: 'Invoice sent', detail: 'Emailed to ' + to });
          this.sendRow = null;
          this.load();
          this.loadInvoiceSummary();
        },
        (e: any) => {
          this.sending = false;
          this.err((e && e.error && e.error.error) || 'Could not send the invoice');
        },
      );
  }

  canCheck(p: any): boolean { return !!p.orderId && (p.status === 'created' || p.status === 'failed'); }

  check(p: any) {
    this.checking = p.id;
    this.svc.checkPayment(p.id).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.checks[p.id] = r; this.checking = null; },
      (e: any) => { this.checking = null; this.err(this.msg(e, 'Could not reach Razorpay')); },
    );
  }

  settle(id: number, after?: () => void) {
    if (!confirm('Mark this payment as paid? The entry will be marked paid, a receipt issued and the school emailed.')) return;
    this.settling = id;
    this.svc.settlePayment(id).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.settling = null;
        this.checks[id] = r.check;
        this.toast.add({ key: 'custom', severity: 'success', summary: 'Payment settled', detail: 'Entry marked paid and receipt issued.' });
        this.load(); this.loadSummary();
        if (after) after();
      },
      (e: any) => { this.settling = null; this.err(this.msg(e, 'Could not settle this payment')); },
    );
  }

  runScan() {
    this.scanning = true; this.scanResult = null;
    this.svc.scanUnrecorded(this.scanDays).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.scanResult = r; this.scanning = false; },
      (e: any) => { this.scanning = false; this.err(this.msg(e, 'Scan failed')); },
    );
  }

  settleFromScan(item: any) {
    this.settle(item.paymentId, () => {
      item.verdict = 'recorded';
    });
  }

  runLookup() {
    if (!this.lookupRef.trim()) return;
    this.lookingUp = true; this.lookupResult = null;
    this.svc.lookupPayment(this.lookupRef.trim(), this.lookupDate || undefined).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.lookupResult = r; this.lookingUp = false; },
      (e: any) => { this.lookingUp = false; this.err(this.msg(e, 'Lookup failed')); },
    );
  }

  /** Opens the matching platform payment in the grid. */
  showRecord(rec: any) {
    this.lookupOpen = false;
    this.status = 'all'; this.purpose = 'all'; this.mode = 'all'; this.from = ''; this.to = '';
    this.q = rec.orderId; this.page = 1; this.load();
  }

  paymentLabel(p: any): string {
    if (p.refunded > 0) return p.refunded >= p.amount ? 'Refunded' : 'Partly refunded';
    return ({ captured: 'Captured', authorized: 'Authorised (not captured)', failed: 'Failed', created: 'Started' } as any)[p.status] || p.status;
  }

  private msg(e: any, fallback: string): string {
    return (e && e.error && (e.error.message || e.error.error)) || fallback;
  }

  private err(m: string) {
    this.toast.add({ key: 'custom', severity: 'error', summary: m });
  }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
